import type { ReactNode } from 'react';
import type { DeckScope } from '../../shared/deck.ts';
import type { DeckHierarchy } from '../lib/deck-page.ts';
import type { AuthFailureCode } from '../lib/auth-client.ts';
import type { DeckSummary } from '../lib/decks.ts';
import { useDesktopSidebarCollapsed } from '../lib/use-desktop-sidebar.ts';
import { cn } from '../lib/utils.ts';
import { AccountControls } from './AccountControls.tsx';
import { DeckNavigation } from './DeckNavigation.tsx';
import { PasskeyMergePrompt } from './PasskeyMergePrompt.tsx';
import { ProjectName } from './ProjectName.tsx';
import {
  mergePromptVisible,
  TelegramMergePrompt,
} from './TelegramMergePrompt.tsx';
import { Button } from './ui/button.tsx';

export function AuthenticatedChrome({
  actionError,
  children,
  decks,
  decksError,
  decksLoading,
  current,
  currentDeckSlug,
  currentDeckScope = {},
  currentDeckHierarchy,
  errorMessage,
  isActing,
  name,
  onAddPasskey,
  onConnectPasskey,
  onConfirmPasskeyMerge,
  onConfirmTelegramMerge,
  onDeclinePasskeyMerge,
  onDeclineTelegramMerge,
  onSignOut,
  onRetryDecks,
  passkeyMergeError,
  passkeyMergeToken,
  showTelegramMerge,
}: {
  actionError: AuthFailureCode | null;
  children: ReactNode;
  decks: DeckSummary[] | undefined;
  decksError: Error | null;
  decksLoading: boolean;
  current: 'home' | 'deck' | 'pdf';
  currentDeckSlug: string | null;
  currentDeckScope?: DeckScope;
  currentDeckHierarchy?: DeckHierarchy | null;
  errorMessage: string | null;
  isActing: boolean;
  name: string | null;
  onAddPasskey: () => void;
  onConnectPasskey?: () => void;
  onConfirmPasskeyMerge: () => void;
  onConfirmTelegramMerge: () => void;
  onDeclinePasskeyMerge: () => void;
  onDeclineTelegramMerge: () => void;
  onSignOut: () => void;
  onRetryDecks: () => void;
  passkeyMergeError: 'merge-conflict' | null;
  passkeyMergeToken: string | null;
  showTelegramMerge: boolean;
}) {
  const { collapsed, toggle } = useDesktopSidebarCollapsed();
  const telegramPrompt = mergePromptVisible(
    showTelegramMerge,
    true,
    actionError,
  );
  return (
    <div
      className={cn(
        'grid min-h-screen bg-background max-[760px]:block',
        collapsed
          ? 'grid-cols-[max-content_minmax(0,1fr)]'
          : 'grid-cols-[minmax(14rem,18rem)_minmax(0,1fr)]',
      )}
    >
      <aside
        className={cn(
          'sticky top-0 grid min-w-0 border-e border-sidebar-border bg-sidebar py-5 [--chrome-inset:1.25rem]',
          collapsed
            ? 'h-auto self-start grid-rows-[auto] border-b rounded-ee-[var(--radius)]'
            : 'h-screen grid-rows-[auto_minmax(0,1fr)_auto]',
          'max-[760px]:relative max-[760px]:grid-rows-[auto_auto_auto] max-[760px]:h-auto max-[760px]:self-auto max-[760px]:border-e-0 max-[760px]:border-b-0 max-[760px]:rounded-none max-[760px]:py-4 max-[760px]:[--chrome-inset:1rem]',
        )}
      >
        <div className="flex items-center gap-2 px-4 ps-[var(--chrome-inset)]">
          <ProjectName isHome={current === 'home'} />
          <Button
            className="ms-auto max-[760px]:hidden"
            onClick={toggle}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            {collapsed ? '→' : '←'}
          </Button>
        </div>
        <div className={collapsed ? 'hidden max-[760px]:contents' : 'contents'}>
          <DeckNavigation
            decks={decks}
            currentDeckSlug={currentDeckSlug}
            currentDeckScope={currentDeckScope}
            currentDeckHierarchy={currentDeckHierarchy}
            error={decksError}
            isLoading={decksLoading}
            onRetry={onRetryDecks}
          />
        </div>
        <div className={collapsed ? 'hidden max-[760px]:contents' : 'contents'}>
          <AccountControls
            errorMessage={errorMessage}
            isActing={isActing}
            name={name}
            onAddPasskey={onAddPasskey}
            onConnectPasskey={onConnectPasskey}
            onSignOut={onSignOut}
          />
        </div>
      </aside>
      <main className="min-w-0 bg-background px-4 py-4 max-[760px]:rounded-none">
        {telegramPrompt ? (
          <TelegramMergePrompt
            actionError={actionError as 'conflict' | 'merge-conflict'}
            isActing={isActing}
            onConfirm={onConfirmTelegramMerge}
            onDecline={onDeclineTelegramMerge}
          />
        ) : null}
        {passkeyMergeToken || passkeyMergeError ? (
          <PasskeyMergePrompt
            error={passkeyMergeError}
            isActing={isActing}
            onConfirm={onConfirmPasskeyMerge}
            onDecline={onDeclinePasskeyMerge}
          />
        ) : null}
        {children}
      </main>
    </div>
  );
}
